import { axiosInstance } from 'utils';
import { BE_GA_MEASUREMENT_REGEX, BE_GA_REGEX } from 'utils/validations';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  PRODUCT_NUMBER_IL,
  PRODUCT_NUMBER_ONLINE,
  PRODUCT_NUMBER_SPL,
} from 'utils/constants';
import { useLocation } from 'react-router-dom';
import Cookie from 'js-cookie';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

export type DebtRestructuringStatus =
  | 'ELIGIBLE'
  | 'NOT_ELIGIBLE'
  | 'ELIGIBLE_INITIATED'
  | 'IN_PROCESS'
  | 'IN_PROCESS_SIGNED'
  | 'IN_PROCESS_PAID'
  | 'ACTIVE'
  | 'ACTIVE_KEPT'
  | 'EXPIRED_BOTH'
  | 'EXPIRED_PAID'
  | 'EXPIRED_SIGNED'
  | 'BROKEN';

export type ProductNumberKindType =
  | typeof PRODUCT_NUMBER_ONLINE
  | typeof PRODUCT_NUMBER_IL
  | typeof PRODUCT_NUMBER_SPL;

// Represents a single product entry returned by the BE with its numeric ID and string kind
export type ProductNumberKind = { number: string; kind: ProductNumberKindType };
// Typed list of all products available to the client (replaces the plain string[] availableProducts)
export type ProductNumberKindAvailables = ProductNumberKind[];

export type Client = {
  id: number;
  number: string;
  debtRestructuringCustomerAction:
    | 'REQUEST_TO_SIGN'
    | 'REQUEST_TO_PAY'
    | 'NO_ACTION';
  debtRestructuringPaymentReceived?: boolean;
  status: 'UNKNOWN' | 'REGISTERED' | 'IDENTIFIED';
  debtRestructuringStatus?: DebtRestructuringStatus;
  actualAddress: {
    postalCode: string;
  };
  declaredAddress: {
    fullAddress: string;
    postalCode: string;
    location1: string;
  };
  documentUploadStarted: boolean;
  mobilePhoneVerified: boolean;
  firstName: string;
  hasActiveIdDocument: boolean;
  lastName: string;
  fullName: string;
  personalId: string;
  hasAttachments: boolean;
  identityDocumentId: string;
  dateOfBirth: string;
  age: number;
  identifiedBy: 'BANK' | 'MANUAL' | 'ID_SERVICE' | 'BROKER';
  registeredBy: 'WEB' | 'OFFLINE' | 'BROKER' | 'CALL_CENTER';
  blocked: boolean;
  identified: boolean;
  bankAccount: string;
  openPrincipalAmount: number;
  homePhone: string;
  validationResolutionDetail:
    | 'PERSONAL_ID_MISSING'
    | 'EXPIRED_IDENTITY_DOCUMENT'
    | 'ERROR'
    | 'CHECK_PAYSLIP'; // NOTE: This is not the full enum, instead only the values currently used on FE side
  productNumber: string;
  additionalAmount: boolean;
  creditLimit: number;
  totalCreditLimit: number;
  availableProducts: string[];
  availableProductsInfo: ProductNumberKindAvailables;
  voidedLoansCount: number;
  hasOpenLoan: boolean;
  productsEligibleForBundleOffer: string[];
  soldDebtMessage?: string;
  mobilePhone: {
    mobilePhone: string;
  };
  email: {
    email: string;
  };
  identificationNumber: string;
  loansCount: number;
  loansCountByProductKind: {
    VIVUS_IL?: number;
    VIVUS_ONLINE?: number;
  };
};

type ClientContextType = {
  client: Client | undefined | null;
  email?: string;
  phone?: string;
  fetchClient: () => Promise<Client>;
  postAnalyticsCookies: () => Promise<void>;
  updateClientPhone: (
    newPhone: string,
  ) => Promise<{ verificationCode: string }>;
  confirmClientPhone: (
    webcode: string,
  ) => Promise<{ status: 'CONFIRMED' | 'REJECT'; message: string }>;
  updateClientAddress: (address: Object) => Promise<void>;
};

const ClientContext = createContext<ClientContextType>({} as ClientContextType);

const useClient = (): ClientContextType => useContext(ClientContext);

const ClientProvider = ({ children }: Props): JSX.Element => {
  const [client, setClient] = useState<Client | undefined | null>();
  const { showError } = useError();
  const location = useLocation();

  const fetchClient = useCallback(async (): Promise<Client> => {
    try {
      const { data: fetchedClient } = await axiosInstance.get('/client');
      setClient(fetchedClient);
      return fetchedClient;
    } catch (e) {
      setClient(null);
      showError();
      throw e;
    }
  }, [showError]);

  useEffect(() => {
    if (client === undefined && isLogged()) fetchClient();
  }, [location]);

  const updateClientPhone = useCallback(
    async (newClientPhone: string) => {
      try {
        const { data: response } = await axiosInstance.post('/client/phone', {
          mobilePhone: newClientPhone,
        });
        return response;
      } catch (e) {
        throw e;
      }
    },
    [client, showError],
  );

  const updateClientAddress = useCallback(
    async (newAddress: Object) => {
      try {
        const { data: response } = await axiosInstance.put(
          '/client/profile',
          newAddress,
        );
        return response;
      } catch (e) {
        throw e;
      }
    },
    [client, showError],
  );

  const confirmClientPhone = useCallback(
    async (webCode: string) => {
      try {
        const { data: response } = await axiosInstance.put(
          '/client/phone-confirmation',
          {
            verificationCode: webCode,
          },
        );
        fetchClient();
        return response;
      } catch (e) {
        throw e;
      }
    },
    [showError],
  );

  const getAnalyticsCookies = useCallback(() => {
    const analyticsCookies = [];

    const ga = Cookie.get('_ga');

    if (ga && BE_GA_REGEX.test(ga)) {
      analyticsCookies.push({
        name: 'ga',
        value: ga,
      });
    }

    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!measurementId) {
      return analyticsCookies;
    }

    const [, secondPart] = measurementId.split('-');

    if (!secondPart) {
      return analyticsCookies;
    }

    const valueCookie = Cookie.get(`_ga_${secondPart}`);

    if (valueCookie && BE_GA_MEASUREMENT_REGEX.test(valueCookie)) {
      analyticsCookies.push({
        name: `ga_${secondPart}`,
        value: valueCookie,
      });
    }

    return analyticsCookies;
  }, []);

  /** Used for tracking client analytic events within COLA */
  const postAnalyticsCookies = useCallback(async () => {
    const gaCookies = getAnalyticsCookies();

    // log to investigate how many clients don't have valid cookies on PROD
    if (gaCookies.length < 2 && import.meta.env.VITE_ENV === 'production') {
      datadogLogs.logger.error(`gacookies missing.`, gaCookies);
    }

    if (gaCookies.length === 0) {
      return;
    }

    try {
      await axiosInstance.post('/client/cookies', gaCookies, {
        headers: {
          'Content-Type': 'application/vnd.analytics.v2+json',
        },
      });
    } catch (e) {
      // Don't show error for cookies call
    }
  }, [getAnalyticsCookies, showError]);

  const clientContextValue = useMemo(
    () => ({
      client,
      confirmClientPhone,
      fetchClient,
      postAnalyticsCookies,
      updateClientPhone,
      updateClientAddress,
    }),
    [
      client,
      confirmClientPhone,
      fetchClient,
      postAnalyticsCookies,
      updateClientPhone,
      updateClientAddress,
    ],
  );

  return (
    <ClientContext.Provider value={clientContextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export { useClient as default, ClientProvider };
